import React from 'react';
import LegalPageLayout from '../components/LegalPageLayout';

const OMBUDSMEN = [
  {
    region: 'Ireland',
    name: 'Financial Services and Pensions Ombudsman (FSPO)',
    address: ['Lincoln House', 'Lincoln Place', 'Dublin 2', 'D02 VH29'],
    phone: '+353 (0)1 567 7000',
    email: 'info@fspo.ie',
    web: 'https://www.fspo.ie',
    note: 'The FSPO independently considers complaints from personal customers and businesses with an annual turnover of less than €3 million. You may be asked to provide a copy of the final response issued by eCOMM Merchant Solutions.',
  },
  {
    region: 'United Kingdom',
    name: 'Financial Ombudsman Service',
    address: ['Exchange Tower', 'Harbour Exchange', 'London', 'E14 9SR'],
    phone: '0800 023 4567',
    email: 'complaint.info@financial-ombudsman.org.uk',
    web: 'https://www.financial-ombudsman.org.uk',
  },
  {
    region: 'Hungary',
    name: 'Pénzügyi Fogyasztóvédelmi Központ',
    address: ['1013 Budapest', 'Krisztina krt. 39. (Krisztina Plaza)'],
    phone: '+36 80 203 776',
    email: 'ugyfelszolgalat@mnb.hu',
    web: 'https://www.mnb.hu',
  },
  {
    region: 'Cyprus',
    name: 'The Financial Ombudsman',
    address: ['13 Lordou Vironos Avenue', '1096 Nicosia'],
    phone: '+357 22 848 900',
    email: 'complaints@financialombudsman.gov.cy',
    web: 'https://www.financialombudsman.gov.cy',
  },
  {
    region: 'Bulgaria',
    name: 'Financial Supervision Commission',
    address: ['No. 16 Budapeshta Str.', '1000 Sofia'],
    phone: '+359 800 40 444',
    email: 'delovodstvo@fsc.bg',
    web: 'https://www.fsc.bg',
  },
  {
    region: 'Greece',
    name: 'The Ombudsman',
    address: ['17 Halkokondyli St.', '104 32 Athens'],
    phone: '+30 213 1306 600',
    email: 'press@synigoros.gr',
    web: 'https://www.synigoros.gr',
  },
  {
    region: 'Poland',
    name: 'Komisja Nadzoru Finansowego',
    address: ['Al. Jerozolimskie 87', '02-001 Warszawa'],
    phone: '+48 22 333 73 26',
    email: 'porady@rf.gov.pl',
    web: 'https://www.rf.gov.pl',
  },
];

const OmbudsmanCard = ({ ombudsman }) => (
  <div className="info-card">
    <h4>{ombudsman.region}</h4>
    <p><strong>{ombudsman.name}</strong></p>
    {ombudsman.address.map((line, i) => <p key={i} style={{ margin: 0 }}>{line}</p>)}
    {ombudsman.phone && (
      <p style={{ marginTop: '0.6rem' }}>
        Telephone: <a href={`tel:${ombudsman.phone.replace(/[^+\d]/g, '')}`}>{ombudsman.phone}</a>
      </p>
    )}
    {ombudsman.email && (
      <p style={{ margin: 0 }}>Email: <a href={`mailto:${ombudsman.email}`}>{ombudsman.email}</a></p>
    )}
    {ombudsman.web && (
      <p style={{ margin: 0 }}>
        Website:{' '}
        <a href={ombudsman.web} target="_blank" rel="noreferrer">
          {ombudsman.web.replace(/^https?:\/\//, '')}
        </a>
      </p>
    )}
    {ombudsman.note && (
      <p style={{ marginTop: '0.75rem', fontStyle: 'italic', color: '#475569' }}>{ombudsman.note}</p>
    )}
  </div>
);

const ComplaintsResolution = () => (
  <LegalPageLayout
    eyebrow="Customer Care · Regulatory"
    title="Complaints & Resolution"
    lead="We are committed to handling concerns promptly and fairly. This page outlines how to raise a complaint, what to expect during our review process and the options available if you are not satisfied with the outcome."
    lastUpdated="June 2026"
    testId="complaints-resolution-page"
  >
    <h2>Complaints Policy</h2>
    <p>
      At eCOMM Merchant Solutions, we are committed to providing a high standard of service. If
      you are dissatisfied with any aspect of our products or services, we encourage you to let
      us know so that we can investigate the matter and work towards a fair resolution.
    </p>

    <h2>How to Make a Complaint</h2>
    <p>
      If you wish to make a complaint, please contact us by email or post, providing as much
      information as possible, including your Merchant Account Number (if applicable).
    </p>
    <div className="info-card">
      <p style={{ margin: 0 }}>
        <strong>Email:</strong> <a href="mailto:complaints@ecomm365.com">complaints@ecomm365.com</a>
      </p>
      <p style={{ marginTop: '0.6rem', marginBottom: 0 }}><strong>Postal Address:</strong></p>
      <p style={{ margin: 0 }}>eCOMM Merchant Solutions</p>
      <p style={{ margin: 0 }}>2 Bedford Place</p>
      <p style={{ margin: 0 }}>Navan, Co. Meath</p>
      <p style={{ margin: 0 }}>C15 VRF3, Ireland</p>
    </div>
    <p>Please include details of your complaint together with any relevant supporting information.</p>

    <h2>What Happens Next?</h2>
    <p>Once we receive your complaint, we will:</p>
    <ul>
      <li>Acknowledge receipt of your complaint within three business days</li>
      <li>Provide the contact details of the person responsible for handling your case</li>
      <li>Explain our complaints handling process</li>
      <li>Investigate the matter thoroughly and, where necessary, request additional information or supporting documents</li>
    </ul>
    <p>
      Where possible, straightforward complaints may be resolved within the initial three
      business days.
    </p>

    <h2>Complaint Resolution</h2>
    <p>
      eCOMM Merchant Solutions Limited ("EMS") and eCOMM Merchant Solutions Ireland Limited
      ("EMSIL") will keep you informed throughout the investigation.
    </p>
    <p>
      We aim to provide a final response within <strong>15 business days</strong> of receiving
      your complaint.
    </p>
    <p>
      In exceptional circumstances, where factors outside our control prevent us from issuing a
      final response within this timeframe, we will send you an interim update explaining the
      reason for the delay and advising when you can expect our final response. In all cases, a
      final response will be provided within <strong>35 business days</strong> of receiving your
      complaint.
    </p>

    <h2>Escalating Your Complaint</h2>
    <p>
      If you are not satisfied with our final response, you may have the right to refer your
      complaint to the relevant Financial Ombudsman in your jurisdiction.
    </p>

    {OMBUDSMEN.map((o) => (
      <OmbudsmanCard key={o.region} ombudsman={o} />
    ))}

    <h2>Our Commitment</h2>
    <p>
      At eCOMM, we take all complaints seriously and view them as an opportunity to improve our
      products and services. We are committed to handling complaints fairly, transparently and
      in accordance with applicable regulatory requirements.
    </p>
  </LegalPageLayout>
);

export default ComplaintsResolution;
